import type { SxProps, Theme } from '@mui/material/styles'
import { uiPalette } from '../../../theme/uiPalette'
import { muahangKiotLike } from '../../layout/muahangKiotTokens'

/**
 * Token bảng danh sách — màu accent đọc từ CSS variables (`ThemeAccentProvider`),
 * fallback trùng `uiPalette` khi chưa hydrate.
 */
export const TABLE_HEAD_BG = `var(--muahang-table-head-bg, ${uiPalette.table.headBg})`
export const TABLE_HEAD_BORDER = `var(--muahang-table-head-border, ${uiPalette.table.headBorder})`
/** Viền ngoài khối bảng + phân trang (theo chủ đề). */
export const TABLE_WRAPPER_BORDER = `var(--muahang-table-wrapper-border, ${uiPalette.table.wrapperBorder})`
/** Kẻ ngang giữa các dòng — nhạt, đỡ rối mắt. */
export const TABLE_ROW_DIVIDER = `var(--muahang-table-cell-border, ${uiPalette.table.cellBorder})`
/** Tiêu đề cột — gần Kiot (đậm, xám đen hơn slate app). */
export const TABLE_HEAD_TEXT = muahangKiotLike.kiotTextPrimary
/** Dòng chẵn (zebra). */
export const TABLE_BODY_BG = '#ffffff'
/** Dòng lẻ — xám rất nhạt. */
export const TABLE_ROW_ALT_BG = '#fafafa'
/** Chữ ô dữ liệu — trùng Kiot oldman (#333). */
export const TABLE_BODY_TEXT = muahangKiotLike.kiotTextPrimary
/** Font weight ô dữ liệu bảng — nổi bật hơn header phụ. */
export const TABLE_BODY_CELL_FONT_WEIGHT = 500
/** Nền vùng chứa khối bảng bo góc — trùng nền sheet trang (tránh wedge trắng ở 4 góc). */
export const TABLE_LIST_CHROME_BG = muahangKiotLike.listSheetBg
export const TABLE_ROW_HOVER_BG = `var(--muahang-row-hover-bg, ${uiPalette.table.rowHoverBg})`
/** Dòng chọn (click) — highlight theo chủ đề. */
export const TABLE_ROW_SELECTED_BG = `var(--muahang-row-selected-bg, ${uiPalette.table.rowHoverBg})`
export const TABLE_ROW_SELECTED_HOVER_BG = 'var(--muahang-row-selected-hover-bg, #e8eef5)'
/** Dòng đang expand (click mở panel) — xanh lá nhạt Kiot (`#00b63e` × 10% + trắng). */
export const TABLE_ROW_EXPANDED_BG = '#e6f8ec'

/** Sx `TableRow` khi isSelected — có hover đậm hơn. */
export const tableListRowSelectedRowSx = {
  '& .MuiTableCell-root': {
    backgroundColor: `${TABLE_ROW_SELECTED_BG} !important`,
  },
  '&:hover .MuiTableCell-root': {
    backgroundColor: `${TABLE_ROW_SELECTED_HOVER_BG} !important`,
  },
} as const

/** Dòng đang chọn + panel expand — không đổi màu khi hover. */
export const tableListRowSelectedRowQuietHoverSx = {
  '& .MuiTableCell-root': {
    backgroundColor: `${TABLE_ROW_SELECTED_BG} !important`,
  },
  '&:hover .MuiTableCell-root': {
    backgroundColor: `${TABLE_ROW_SELECTED_BG} !important`,
  },
} as const

/** Dòng tổng (summary) — cam nhạt kiểu Kiot (`#fff3e6`). */
export const TABLE_SUMMARY_BG = muahangKiotLike.invoiceSummaryRowBg
export const TABLE_PAGINATION_BG = '#ffffff'
/** Trang hiện tại: nút xanh lá Kiot Hàng hóa. */
export const TABLE_PAGINATION_ACTIVE_BG = muahangKiotLike.posButtonGreen
export const TABLE_PAGINATION_ACTIVE_HOVER = muahangKiotLike.posButtonGreenHover
/** Bo góc nhẹ khối bảng + phân trang (một surface). */
export const TABLE_LIST_SURFACE_RADIUS_PX = 8
/** Bo góc nút trang trong `Pagination` (nhẹ). */
export const TABLE_PAGINATION_ITEM_RADIUS_PX = 4
export const TABLE_CONTROL_BORDER = '#d0d0d0'
export const TABLE_CONTROL_BORDER_HOVER = '#b0b0b0'

/** @deprecated Alias — dùng cho kẻ dọc/ngang ô. */
export const TABLE_CELL_BORDER = TABLE_ROW_DIVIDER

/** Viền mép cụm cột cố định (cạnh phải cột ghim trái cuối / cạnh trái cột ghim phải đầu). */
export const TABLE_FIXED_EDGE_BORDER_PX = 2
export const TABLE_FIXED_EDGE_BORDER = '#c4cad3'

/** Inset shadow kẻ mép cụm cố định — `side` là phía tiếp giáp vùng cuộn. */
export function tableFixedEdgeInsetShadow(side: 'left' | 'right'): string {
  const x = side === 'right' ? -TABLE_FIXED_EDGE_BORDER_PX : TABLE_FIXED_EDGE_BORDER_PX
  return `inset ${x}px 0 0 0 ${TABLE_FIXED_EDGE_BORDER}`
}

/** Chiều cao track scroll ngang (macOS không custom được scrollbar trên vùng cao). */
export const TABLE_LIST_SCROLLBAR_SIZE_PX = 6

export const tableListScrollShellSx: SxProps<Theme> = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  minWidth: 0,
  '--muahang-table-scrollbar-size': `${TABLE_LIST_SCROLLBAR_SIZE_PX}px`,
}

/** Vùng cuộn chính (dọc + ngang). */
export const tableListScrollMainSx: SxProps<Theme> = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  overscrollBehavior: 'contain',
  overflowAnchor: 'none',
  scrollbarGutter: 'stable',
  touchAction: 'pan-x pan-y',
  bgcolor: TABLE_BODY_BG,
  backgroundImage: `linear-gradient(${TABLE_HEAD_BG}, ${TABLE_HEAD_BG})`,
  backgroundSize: '100% 42px',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: '0 0',
}

/** @deprecated Dùng `tableListScrollMainSx` + `TableListScrollArea`. */
export const tableListScrollRootSx = tableListScrollMainSx

export const tableListScrollAreaSx: SxProps<Theme> = {}
